import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, TextField } from '@mui/material';
import { TranscriptRow } from '@/types/transcriptionTypes';
import { TranscriptionSession } from '@/types/transcriptionSessionTypes';
import { updateTranscriptionSession } from '@/services/transcriptionSessionService';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';

interface NotesInterfaceProps {
  session?: TranscriptionSession | null;
  speakers?: TranscriptRow[];
  currentPlaybackTime?: number;
  onTimestampClick?: (timestamp: number) => void;
  compact?: boolean;
  onNotesSaved?: () => void;
}

export const NotesInterface: React.FC<NotesInterfaceProps> = ({
  session,
  speakers = [],
  currentPlaybackTime = 0,
  onTimestampClick,
  compact = false,
  onNotesSaved
}) => {
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const textFieldRef = useRef<HTMLTextAreaElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUserEditingRef = useRef(false);
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser } = useAppUser();

  // Load existing notes when session changes (but only if user is not actively editing)
  useEffect(() => {
    // Don't override user's edits if they're actively typing
    if (isUserEditingRef.current) return;
    
    if (session?.notes) {
      setNotes(session.notes);
    } else {
      setNotes('');
    }
  }, [session?.id]); // Removed session?.notes dependency to prevent resetting after save

  // Auto-focus when component mounts and restore focus if it was editing
  useEffect(() => {
    if (textFieldRef.current && isUserEditingRef.current) {
      textFieldRef.current.focus();
    }
  }, [session?.id]); // Re-focus when session updates

  // Auto-save function with debounce
  const saveNotes = useCallback(
    async (notesText: string) => {
      if (!session?.id || !appUser?.id) return;

      try {
        setIsSaving(true);
        await updateTranscriptionSession(
          session.id,
          {
            id: session.id,
            notes: notesText
          },
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        onNotesSaved?.();
      } catch (error) {
        console.error('Error saving notes:', error);
      } finally {
        setIsSaving(false);
        // Clear editing flag after save completes
        isUserEditingRef.current = false;
      }
    },
    [
      session?.id,
      appUser?.id,
      getAccessTokenSilently,
      getAccessTokenWithPopup,
      onNotesSaved
    ]
  );

  const handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newNotes = event.target.value;
    setNotes(newNotes);
    
    // Mark that user is actively editing
    isUserEditingRef.current = true;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for auto-save (1 second debounce)
    saveTimeoutRef.current = setTimeout(() => {
      saveNotes(newNotes);
    }, 1000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Support common text editing shortcuts
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'b':
          // Bold text (markdown style)
          event.preventDefault();
          insertMarkdown('**', '**');
          break;
        case 'i':
          // Italic text (markdown style)
          event.preventDefault();
          insertMarkdown('*', '*');
          break;
        case 'k':
          // Link (markdown style)
          event.preventDefault();
          insertMarkdown('[', '](url)');
          break;
      }
    }

    // Handle Tab for indentation
    if (event.key === 'Tab') {
      event.preventDefault();
      insertText('  '); // Two spaces for indentation
    }
  };

  const insertMarkdown = (prefix: string, suffix: string) => {
    const textarea = textFieldRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = notes.substring(start, end);
    const newText =
      notes.substring(0, start) +
      prefix +
      selectedText +
      suffix +
      notes.substring(end);

    setNotes(newText);

    // Set cursor position after the prefix
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + prefix.length, end + prefix.length);
    }, 0);
  };

  const insertText = (text: string) => {
    const textarea = textFieldRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newText = notes.substring(0, start) + text + notes.substring(end);

    setNotes(newText);

    // Set cursor position after the inserted text
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <TextField
        fullWidth
        multiline
        placeholder="Write notes here that will get added to the record..."
        variant="outlined"
        value={notes}
        onChange={handleNotesChange}
        onKeyDown={handleKeyDown}
        inputRef={textFieldRef}
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': {
            height: '100%',
            alignItems: 'flex-start',
            padding: 0,
            '& fieldset': {
              border: 'none'
            },
            '&:hover fieldset': {
              border: 'none'
            },
            '&.Mui-focused fieldset': {
              border: 'none'
            }
          },
          '& .MuiInputBase-input': {
            height: '100% !important',
            padding: '16px',
            fontSize: '14px',
            lineHeight: 1.5,
            overflow: 'auto !important',
            resize: 'none',
            fontFamily: 'inherit'
          },
          '& .MuiInputBase-input::placeholder': {
            fontSize: '14px !important'
          }
        }}
      />
      {/* Subtle saving indicator that doesn't block typing */}
      {isSaving && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            fontSize: '11px',
            color: 'text.secondary',
            opacity: 0.6,
            pointerEvents: 'none',
            backgroundColor: 'background.paper',
            px: 1,
            py: 0.5,
            borderRadius: '4px'
          }}
        >
          Saving...
        </Box>
      )}
    </Box>
  );
};

export default NotesInterface;
